import { useEffect, useMemo, useState } from "react";
import { Box, Button } from "@mui/material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { GovItemDetail, GovSettingsOn, GovUatTest, getItem } from "../../../../utils/icGovernance";
import { useGovernance } from "../GovernanceLayout";
import {
  CARD_BORDER,
  PRIMARY,
  SUPPORT_BASE,
  SignOffChip,
  StatusChip,
  clientLabel,
  dateOnly,
  itemUrl,
  itemUrlById,
  linkedIds,
  statusLabel,
  vendorLabel,
  yesNo,
} from "../governanceStatus";
import NewUatTestDialog, { ReleaseOption } from "../dialogs/NewUatTestDialog";
import { NewButton } from "../dialogs/dialogParts";
import { IdLink, INK, LinkedIdList, LongText, MONO, MUTED, ReleaseLink, PlainText, localDay } from "./cells";
import RegisterTable, { RegisterColumn, WaitingNotice } from "./RegisterTable";
import { useRegisterItems } from "./useRegisterItems";

// UAT and Sign-Off — columns in the client's workbook order.
const uatColumns = (s: GovSettingsOn): RegisterColumn<GovUatTest>[] => [
  { key: "id", header: "Test ID", render: (r) => <IdLink item={r} /> },
  { key: "release", header: "Release ID", render: (r) => <ReleaseLink release={r.release} /> },
  { key: "requirement", header: "Requirement / Feature", wrap: true, minWidth: 220, render: (r) => <LongText value={r.requirement || r.title} /> },
  { key: "scenario", header: "Test Scenario", wrap: true, render: (r) => <LongText value={r.scenario} /> },
  {
    key: "preconditions",
    header: "Preconditions / Test Data",
    wrap: true,
    hideByDefault: true,
    render: (r) => <LongText value={r.preconditions} />,
  },
  {
    key: "expected",
    header: "Expected Result",
    wrap: true,
    hideByDefault: true,
    render: (r) => <LongText value={r.expectedResult} />,
  },
  {
    key: "actual",
    header: "Actual Result",
    wrap: true,
    hideByDefault: true,
    render: (r) => <LongText value={r.actualResult} />,
  },
  { key: "result", header: "Result", render: (r) => <StatusChip kind="UAT_TEST" status={r.status} /> },
  { key: "relatedIssue", header: "Related Issue ID", render: (r) => <LinkedIdList ids={linkedIds(r, ["ISSUE"])} /> },
  { key: "tester", header: `${clientLabel(s)} Tester`, render: (r) => <PlainText value={r.clientOwner} /> },
  { key: "testDate", header: "Test Date", render: (r) => dateOnly(r.testDate) },
  { key: "support", header: `${vendorLabel(s)} Support`, render: (r) => <PlainText value={r.vendorOwner} /> },
  { key: "retestRequired", header: "Retest Required", render: (r) => yesNo(r.retestRequired) },
  {
    key: "retestResult",
    header: "Retest Result",
    render: (r) => <PlainText value={r.retestResult ? statusLabel(r.retestResult) : null} />,
  },
  { key: "signOffBy", header: `${clientLabel(s)} Sign-Off By`, render: (r) => <PlainText value={r.signOff?.decidedBy} /> },
  { key: "signOffStatus", header: "Sign-Off Status", render: (r) => <SignOffChip status={r.signOff?.status} /> },
  { key: "signOffDate", header: "Sign-Off Date", render: (r) => localDay(r.signOff?.decidedAt) },
];

const WAITING: WaitingNotice<GovUatTest> = {
  reason: (r) => (r.retestRequired ? "retest" : r.status === "NOT_RUN" ? "not run" : null),
  describe: (n, reason) =>
    reason === "retest"
      ? `${n} ${n === 1 ? "test needs" : "tests need"} a retest`
      : `${n} ${n === 1 ? "test has" : "tests have"} not been run yet`,
};

type BannerProps = { release: ReleaseOption | null; loading: boolean; allUrl: string };

/** "Tests of release TD-REL-2 v2.4.0 — {title}" above a filtered register. */
const ReleaseBanner = ({ release, loading, allUrl }: BannerProps) => (
  <Box
    sx={{
      mb: 2,
      px: 1.75,
      py: 1.25,
      border: CARD_BORDER,
      borderRadius: "8px",
      backgroundColor: "#fafafb",
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 1,
      color: INK,
    }}
  >
    <Box component="span" sx={{ fontWeight: 600 }}>
      Tests of release
    </Box>
    {release ? (
      <>
        <Link
          to={itemUrlById(release.displayId || release.id)}
          style={{ fontFamily: MONO, fontWeight: 700, color: PRIMARY, textDecoration: "none" }}
        >
          {release.displayId || "Release"}
        </Link>
        {release.version ? <span>v{release.version}</span> : null}
        <Box component="span" sx={{ color: MUTED }}>
          — {release.title || "(untitled)"}
        </Box>
      </>
    ) : (
      <Box component="span" sx={{ color: MUTED }}>
        {loading ? "…" : "(this release)"}
      </Box>
    )}
    <Button component={Link} to={allUrl} size="small" sx={{ ml: "auto", textTransform: "none", color: PRIMARY }}>
      Show all tests
    </Button>
  </Box>
);

const UatRegister = () => {
  const { settings } = useGovernance();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const releaseParam = searchParams.get("release") || null;
  const register = useRegisterItems<GovUatTest>("UAT_TEST", { release: releaseParam });
  const columns = useMemo(() => uatColumns(settings), [settings]);
  const [newOpen, setNewOpen] = useState(false);

  // Name the release the register is filtered to.
  const [fetchedRelease, setFetchedRelease] = useState<ReleaseOption | null>(null);
  const [releaseLoading, setReleaseLoading] = useState(false);
  useEffect(() => {
    setFetchedRelease(null);
    if (!releaseParam) return;
    let alive = true;
    setReleaseLoading(true);
    getItem(releaseParam)
      .then((item) => {
        if (alive && item?.kind === "RELEASE") {
          setFetchedRelease({ id: item.id, displayId: item.displayId, version: item.version, title: item.title });
        }
      })
      .catch((err) => console.error("Failed to load the release for the UAT filter:", err))
      .finally(() => {
        if (alive) setReleaseLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [releaseParam]);

  // Fallback: the first row's own release reference.
  const firstRowRelease = register.rows.find((r) => r.release && r.release.id === releaseParam)?.release ?? null;
  const release: ReleaseOption | null = releaseParam ? fetchedRelease ?? firstRowRelease : null;

  const allUrl = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete("release");
    const qs = params.toString();
    return `${SUPPORT_BASE}/uat${qs ? `?${qs}` : ""}`;
  }, [searchParams]);

  const onCreated = (item: GovItemDetail) => {
    setNewOpen(false);
    navigate(itemUrl(item));
  };

  return (
    <>
      <RegisterTable
        {...register}
        kind="UAT_TEST"
        columns={columns}
        noun={{ one: "test", many: "tests" }}
        caption={`${clientLabel(settings)} tests and signs off; ${vendorLabel(settings)} supports`}
        actions={<NewButton label="New test" onClick={() => setNewOpen(true)} />}
        waiting={WAITING}
        banner={
          releaseParam ? <ReleaseBanner release={release} loading={releaseLoading} allUrl={allUrl} /> : null
        }
      />
      <NewUatTestDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={onCreated}
        defaultRelease={release}
      />
    </>
  );
};

export default UatRegister;
